import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ACTOR_THEME } from "@/components/ActivitiesBoard";
import { agentColor, agentHsl, registerAgents } from "@/lib/agentColors";
import AgendaCalendar from "@/components/AgendaCalendar";
import OverdueVisitsDialog from "@/components/OverdueVisitsDialog";
import AffiancamentoButton from "@/components/AffiancamentoButton";
import { VisiteDaProgrammareDialog } from "@/components/VisiteDaProgrammareDialog";
import QuickAdd from "@/components/QuickAdd";
import VisitaB2BDialog from "@/components/VisitaB2BDialog";
import ProspectRecontactDialog from "@/components/ProspectRecontactDialog";
import RecallPreventiviDialog from "@/components/RecallPreventiviDialog";
import VisiteFunnelDialog from "@/components/VisiteFunnelDialog";
import { buildDaProgrammare } from "@/lib/daProgrammare";
import { Button } from "@/components/ui/button";
import { CalendarDays, Loader2, CalendarClock, ClipboardCheck, Clock, FileClock } from "lucide-react";
import FunnelTriggerButton from "@/components/FunnelTriggerButton";

const TYPE_LABEL = { affiancamento_agenzia: "Affiancamento agenzia", visita_partner: "Visita", telefonata: "Telefonata", videocall: "Video call", email_contatto: "Email / contatto", review_partner: "Review Partner" };

const actorOf = (it) => (it.actor === "agenzia" ? "agenzia" : "responsabile_commerciale");
const agencyOf = (it) => it.agency_name || it.resolved_agency_name || null;
const targetName = (it) => (it.masked ? "Occupato" : (it.partner_name || it.prospect_name || it.agency_name) || "—");

export default function Agenda() {
  const { user } = useAuth();
  const insertActor = user?.role === "agency" ? "agenzia" : "responsabile_commerciale";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visitaOpen, setVisitaOpen] = useState(false);
  const [partners, setPartners] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [managers, setManagers] = useState([]);
  const [actorFilter, setActorFilter] = useState("all");
  const [agentFilter, setAgentFilter] = useState("all");
  const [agencyFilter, setAgencyFilter] = useState("all");
  const [agencies, setAgencies] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [view, setView] = useState("2weeks");
  const [showDaProg, setShowDaProg] = useState(false);
  const [showProspect, setShowProspect] = useState(false);
  const [showRecall, setShowRecall] = useState(false);
  const [showVisiteFunnel, setShowVisiteFunnel] = useState(false);
  const isDirez = ["admin", "commercial_manager", "head_of_partner", "back_office"].includes(user?.role);
  const [recallItems, setRecallItems] = useState([]);
  const [visitaDefault, setVisitaDefault] = useState({ partnerId: null, prospectId: null });
  const [qaOpen, setQaOpen] = useState(false);
  const [qaDefault, setQaDefault] = useState({ partnerId: null, partnerName: "", prospectId: null, prospectName: "" });
  const openAttivitaFor = (rec) => {
    setShowDaProg(false); setShowProspect(false);
    setQaDefault({
      partnerId: rec.kind === "partner" ? rec.id : null,
      partnerName: rec.kind === "partner" ? rec.name : "",
      prospectId: rec.kind === "prospect" ? rec.id : null,
      prospectName: rec.kind === "prospect" ? rec.name : "",
    });
    setQaOpen(true);
  };
  const openVisitaFor = (rec) => {
    setShowDaProg(false); setShowProspect(false);
    setVisitaDefault({ partnerId: rec.kind === "partner" ? rec.id : null, prospectId: rec.kind === "prospect" ? rec.id : null });
    setVisitaOpen(true);
  };

  useEffect(() => {
    api.get("/activities?limit=3000").then((r) => { setItems(r.data.items || r.data); setLoading(false); }).catch(() => setLoading(false));
    api.get("/agencies").then((r) => { const ags = r.data || []; setAgencies(ags); registerAgents(ags.map((a) => a.name)); }).catch(() => {});
    api.get("/partners").then((r) => setPartners(r.data || [])).catch(() => {});
    api.get("/prospects").then((r) => setProspects(r.data || [])).catch(() => {});
    if (user?.role === "admin") api.get("/users?role=commercial_manager").then((r) => setManagers(r.data || [])).catch(() => {});
    api.get("/activities/overdue-visits").then((r) => { const list = r.data.items || []; setOverdue(list); if (list.length) setOverdueOpen(true); }).catch(() => {});
  }, []);

  const [reactItems, setReactItems] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [overdueOpen, setOverdueOpen] = useState(false);
  const reloadOverdue = () => api.get("/activities/overdue-visits").then((r) => setOverdue(r.data.items || [])).catch(() => {});
  useEffect(() => {
    api.get("/agenda/riattivazione").then((r) => setReactItems(r.data.items || [])).catch(() => setReactItems([]));
    api.get("/quotes/recall").then((r) => setRecallItems(r.data.items || [])).catch(() => setRecallItems([]));
  }, []);

  const didInitView = useRef(false);
  useEffect(() => {
    if (!didInitView.current && user?.role) {
      setView("2weeks");
      didInitView.current = true;
    }
  }, [user]);

  const prospectDue = useMemo(() => {
    const t = new Date().toISOString().slice(0, 10);
    const HIDDEN = ["vinto", "ordine", "perso"];
    return (prospects || [])
      .filter((p) => p.next_action_date && String(p.next_action_date).slice(0, 10) <= t && !HIDDEN.includes(p.stage))
      .sort((a, b) => String(a.next_action_date).localeCompare(String(b.next_action_date)));
  }, [prospects]);

  const reload = () => api.get("/activities?limit=3000").then((r) => setItems(r.data.items || r.data)).catch(() => {});

  // Le visite cicliche generate dal programma non compaiono nel calendario: solo attività reali.
  const daProgrammare = useMemo(() => buildDaProgrammare(reactItems, items), [items, reactItems]);

  const filtered = useMemo(() => {
    const seen = new Set();
    // id delle visite presenti: nella vista "Tutti" l'affiancamento collegato viene unito alla visita
    // (che mostra già il badge «Aff») così si vede un solo impegno per slot, senza doppioni.
    const visitIds = new Set(items.filter((it) => it.type === "visita" || it.type === "visita_partner").map((it) => it.id));
    return items.filter((it) => {
      if (it.generated) return false;
      if (actorFilter === "all" && it.type === "affiancamento_agenzia" && it.affiancamento_visit_id && visitIds.has(it.affiancamento_visit_id)) return false;
      if (actorFilter !== "all" && actorOf(it) !== actorFilter) return false;
      if (agencyFilter !== "all" && agencyOf(it) !== agencyFilter) return false;
      if (agentFilter !== "all" && (it.owner_id || "none") !== agentFilter) return false;
      if (statusFilter !== "all" && it.status !== statusFilter) return false;
      if (it.id && seen.has(it.id)) return false;
      if (it.id) seen.add(it.id);
      return true;
    });
  }, [items, actorFilter, agencyFilter, agentFilter, statusFilter]);

  // Un colore distinto e deterministico per ogni agenzia; il Responsabile resta arancione.
  const ownerColors = useMemo(() => {
    const m = {};
    items.forEach((it) => {
      if (actorOf(it) !== "agenzia") return;
      const key = agencyOf(it);
      if (!key || m[key]) return;
      m[key] = { name: key, hex: agentHsl(key) };
    });
    return m;
  }, [items]);

  const agents = useMemo(() => {
    const m = {};
    items.forEach((it) => { if (it.owner_id && it.owner_name) m[it.owner_id] = it.owner_name; });
    return Object.entries(m).sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((it) => { const d = it.date || "Senza data"; (map[d] = map[d] || []).push(it); });
    return Object.entries(map).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [filtered]);

  if (loading)
    return <div className="flex h-full items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="agenda-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <CalendarDays className="h-6 w-6 text-primary" />
          <div>
            <h1 className="font-heading text-3xl font-black tracking-tight">Agenda</h1>
            <p className="text-sm text-muted-foreground">Tutte le attività, distinte per colore: un colore per ogni agente e arancione per il responsabile commerciale.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 rounded-lg border border-[#FDD003]/60 bg-[#FDD003]/[0.04] p-1.5" data-testid="agenda-group-crea">
            <QuickAdd onChanged={reload} show={["activity"]} />
            <Button size="sm" onClick={() => { setVisitaDefault({ partnerId: null, prospectId: null }); setVisitaOpen(true); }} data-testid="agenda-registra-visita-btn">
              <ClipboardCheck className="mr-2 h-4 w-4" /> Registra visita
            </Button>
          </div>
          <div data-testid="agenda-group-prospect">
            <QuickAdd onChanged={reload} show={["prospect"]} variant="default" />
          </div>
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#FDD003]/60 bg-[#FDD003]/[0.04] p-1.5" data-testid="agenda-group-liste">
            <Button variant="outline" size="sm" onClick={() => setShowDaProg(true)} data-testid="agenda-da-programmare-btn">
              <CalendarClock className="mr-2 h-4 w-4" /> Visite da programmare
              {daProgrammare.length > 0 && <Badge className="ml-2 bg-primary text-primary-foreground">{daProgrammare.length}</Badge>}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowProspect(true)} data-testid="agenda-prospect-recall-btn">
              <Clock className="mr-2 h-4 w-4" /> Prospect da ricontattare
              {prospectDue.length > 0 && <Badge className="ml-2 bg-primary text-primary-foreground">{prospectDue.length}</Badge>}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setShowRecall(true)} data-testid="agenda-preventivi-recall-btn">
              <FileClock className="mr-2 h-4 w-4" /> Recall preventivi
              {recallItems.length > 0 && <Badge className="ml-2 bg-primary text-primary-foreground">{recallItems.length}</Badge>}
            </Button>
          </div>
          {isDirez && (
            <div className="rounded-lg border border-emerald-500/50 bg-emerald-500/[0.06] p-1.5" data-testid="agenda-group-funnel">
              <FunnelTriggerButton label="Funnel visite" onClick={() => setShowVisiteFunnel(true)} data-testid="agenda-visite-funnel-btn" />
            </div>
          )}
          <div className="flex rounded-md border border-border p-0.5" data-testid="agenda-view-toggle">
            {[["2weeks", "2 settimane"], ["month", "Mese"], ["week", "Settimana"], ["list", "Elenco"]].map(([v, l]) => (
              <button key={v} onClick={() => setView(v)} data-testid={`agenda-view-${v}`}
                className={`rounded px-3 py-1 text-xs font-medium transition-colors ${view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1" data-testid="agenda-legend">
            <button type="button" onClick={() => { setActorFilter((f) => f === "responsabile_commerciale" ? "all" : "responsabile_commerciale"); setAgencyFilter("all"); }} className={`flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium transition-colors ${actorFilter === "responsabile_commerciale" ? "bg-primary/15 ring-1 ring-primary" : "hover:bg-muted"}`} data-testid="agenda-legend-resp"><span className={`h-3 w-3 rounded-full ${ACTOR_THEME.responsabile_commerciale.dot}`} /> Resp. Commerciale</button>
            {Object.values(ownerColors).map((oc, i) => (
              <button key={i} type="button" onClick={() => { setAgencyFilter((f) => f === oc.name ? "all" : oc.name); setActorFilter("all"); }} className={`flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium transition-colors ${agencyFilter === oc.name ? "bg-primary/15 ring-1 ring-primary" : "hover:bg-muted"}`} data-testid="agenda-legend-agent"><span className="h-3 w-3 rounded-full" style={{ backgroundColor: oc.hex }} /> {oc.name}</button>
            ))}
          </div>
          <Select value={actorFilter} onValueChange={setActorFilter}>
            <SelectTrigger className="h-9 w-[170px]" data-testid="agenda-actor-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli attori</SelectItem>
              <SelectItem value="agenzia">Agenzia</SelectItem>
              <SelectItem value="responsabile_commerciale">Resp. Commerciale</SelectItem>
            </SelectContent>
          </Select>
          <Select value={agencyFilter} onValueChange={setAgencyFilter}>
            <SelectTrigger className="h-9 w-[190px]" data-testid="agenda-agency-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le agenzie</SelectItem>
              {agencies.map((a) => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {agents.length > 1 && (
            <Select value={agentFilter} onValueChange={setAgentFilter}>
              <SelectTrigger className="h-9 w-[180px]" data-testid="agenda-agent-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli agenti</SelectItem>
                {agents.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-[150px]" data-testid="agenda-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="pianificato">Pianificate</SelectItem>
              <SelectItem value="svolto">Svolte</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {view === "list" && grouped.length === 0 && <Card className="p-10 text-center text-muted-foreground">Nessuna attività per i filtri selezionati.</Card>}

      {view === "list" && grouped.map(([date, list]) => (
        <div key={date} className="space-y-2">
          <div className="font-mono text-xs font-semibold uppercase tracking-widest text-muted-foreground">{date}</div>
          <div className="space-y-2">
            {list.sort((a, b) => (a.time || "").localeCompare(b.time || "")).map((it) => {
              const th = ACTOR_THEME[actorOf(it)];
              // Gli affiancamenti sono attività del RESPONSABILE: usano il colore/tema del
              // responsabile (arancione), non quello dell'agenzia dell'agente.
              const oc = actorOf(it) === "agenzia" ? ownerColors[agencyOf(it)] : null;
              return (
                <Card key={it.id} className={`border-l-4 p-4 ${oc ? "" : th.border}`} style={oc ? { borderLeftColor: oc.hex } : undefined} data-testid={`agenda-item-${it.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <span className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${oc ? "" : th.dot}`} style={oc ? { backgroundColor: oc.hex } : undefined} />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{targetName(it)}</span>
                          <Badge variant="outline" className={`text-[10px] ${th.badge}`}>{th.label}</Badge>
                          {agencyOf(it) && oc && <Badge variant="outline" className="text-[10px]" style={{ borderColor: oc.hex, color: oc.hex }} data-testid={`agenda-owner-badge-${it.id}`}>{agencyOf(it)}</Badge>}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {TYPE_LABEL[it.type]}{it.resolved_agency_name ? ` · ${it.resolved_agency_name}` : ""}{it.owner_name ? ` · ${it.owner_name}` : ""}
                        </div>
                        {(it.outcome || it.notes || it.prossima_azione) && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            {it.outcome && <span className="mr-3">Esito: {it.outcome}</span>}
                            {it.prossima_azione && <span className="mr-3">Prossima: {it.prossima_azione}{it.data_prossima_azione ? ` (${it.data_prossima_azione})` : ""}</span>}
                            {it.notes && <span>{it.notes}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {it.time && <span className="font-mono text-xs text-muted-foreground">{it.time}</span>}
                      <Badge variant={it.status === "svolto" ? "default" : it.status === "non_svolta" ? "destructive" : "secondary"} className="text-[11px]">{it.status === "svolto" ? "Svolto" : it.status === "non_svolta" ? "Non svolta" : "Pianificato"}</Badge>
                      <AffiancamentoButton visit={it} onDone={reload} />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      ))}

      {view !== "list" && <Card className="p-4"><AgendaCalendar items={filtered} mode={view} ownerColors={ownerColors} onChanged={reload} kmFilters={{ actor: actorFilter, agency: agencyFilter, agent: agentFilter, status: statusFilter }} /></Card>}

      <OverdueVisitsDialog open={overdueOpen} onOpenChange={setOverdueOpen} items={overdue} onDone={() => { reload(); reloadOverdue(); }} />
      <VisiteDaProgrammareDialog open={showDaProg} onOpenChange={setShowDaProg} items={daProgrammare} onChanged={reload} actor={insertActor} ownerId={user?.role === "agency" ? user.id : null} onNuovaAttivita={openAttivitaFor} onRegistraVisita={openVisitaFor} />
      <ProspectRecontactDialog open={showProspect} onOpenChange={setShowProspect} items={prospectDue} onNuovaAttivita={openAttivitaFor} onRegistraVisita={openVisitaFor} />
      <RecallPreventiviDialog open={showRecall} onOpenChange={setShowRecall} items={recallItems} />
      <VisiteFunnelDialog open={showVisiteFunnel} onOpenChange={setShowVisiteFunnel} />
      <VisitaB2BDialog open={visitaOpen} onOpenChange={setVisitaOpen} partners={partners} prospects={prospects} managers={managers} actor={insertActor} defaultPartnerId={visitaDefault.partnerId} defaultProspectId={visitaDefault.prospectId} onSaved={reload} />
      <QuickAdd hideTriggers activityOpen={qaOpen} onActivityOpenChange={setQaOpen} partnerId={qaDefault.partnerId} partnerName={qaDefault.partnerName} prospectId={qaDefault.prospectId} prospectName={qaDefault.prospectName} onChanged={reload} show={["activity"]} />
    </div>
  );
}
